/**
 * Pure helpers for the Purchase Order Analysis page (no React, no I/O).
 * Calculations live in the backend (control-tower api/purchase-order-analysis);
 * this only shapes requests and formats the response.
 */
import { RISK_BY_KEY, enabledRiskBands, fmtUnits } from "./inventoryRiskPageDefaults";

export { fmtDays, fmtMoney, fmtUnits } from "./inventoryRiskPageDefaults";

// Coverage of an at-risk SKU by open POs, best to worst. Colours were run
// through the dataviz palette validator (light + dark); "unconfirmed" is gray on
// purpose — those POs are shown but not counted as supply. Always paired with a
// label, never colour alone.
export const COVERAGE_SEGMENTS = [
  {
    key: "covered",
    short: "Covered",
    label: "Covered — a PO lands before stock runs out",
    light: "#1C7A4E",
    dark: "#2E9E74",
  },
  {
    key: "pending",
    short: "On order, no firm date",
    label: "On order, but overdue (≤30 days) or undated",
    light: "#3B6FE0",
    dark: "#5B87E3",
  },
  {
    key: "late",
    short: "Arrives too late",
    label: "Next PO lands after the stock-out day",
    light: "#E8590C",
    dark: "#DB6A2C",
  },
  {
    key: "unconfirmed",
    short: "Stale PO only",
    label: "Only POs overdue by more than 30 days — not counted",
    light: "#8A8FA3",
    dark: "#868CA0",
  },
  {
    key: "none",
    short: "No PO",
    label: "Nothing on order",
    light: "#B5203A",
    dark: "#DC5068",
  },
];

export const COVERAGE_BY_KEY = Object.fromEntries(
  COVERAGE_SEGMENTS.map((segment) => [segment.key, segment]),
);

export const coverageColor = (key, darkMode) => {
  const segment = COVERAGE_BY_KEY[key];
  if (!segment) return "#94A3B8";
  return darkMode ? segment.dark : segment.light;
};

// Out of stock for the whole 30-day window, sold before it (backend group).
export const LONG_STOCKOUT = "out_of_stock_long";

const RISK_ROW_NAME = { critical: "Critical", at_risk: "At risk", watch: "Watch", low: "Low" };

/**
 * Coverage-matrix row labels for the client's risk buckets (the API's
 * `risk_bands`; today's defaults without it). Buckets the client switched off
 * have no label, and their (always empty) matrix rows are not shown.
 */
export const riskRowLabels = (riskBands) => {
  const labels = { [LONG_STOCKOUT]: "Out all month", out_of_stock: "Out of stock" };
  let lower = null;
  enabledRiskBands(riskBands).forEach(([key, upper]) => {
    labels[key] = lower == null ? `${RISK_ROW_NAME[key]} ≤${upper}d` : `${RISK_ROW_NAME[key]} ${lower}–${upper}d`;
    lower = upper;
  });
  labels.healthy = `${lower}d to horizon`;
  return labels;
};

export const RISK_ROW_LABEL = riskRowLabels();

// Darker than Out of Stock: same condition, longer. Always shown with its label.
const LONG_STOCKOUT_COLOR = "#4A0E0E";

export const riskColor = (key) =>
  key === LONG_STOCKOUT ? LONG_STOCKOUT_COLOR : RISK_BY_KEY[key]?.color || "#94A3B8";

export const COVERAGE_FILTERS = [
  { value: "all", label: "All at-risk SKUs" },
  { value: "needs_po", label: "Needs a PO" },
  { value: "late", label: "Arrives too late" },
  { value: "pending", label: "No firm date" },
  { value: "covered", label: "Covered" },
];

export const PO_GROUP_TABS = [
  { value: "open", label: "Open" },
  { value: "overdue", label: "Overdue" },
  { value: "received", label: "Received" },
  { value: "all", label: "All" },
];

export const LOOKBACK_OPTIONS = [
  { value: 3, label: "3 months" },
  { value: 6, label: "6 months" },
  { value: 12, label: "12 months" },
  { value: 24, label: "24 months" },
];
export const DEFAULT_LOOKBACK = 12;

export const STAGE_LABEL = {
  placed: "Placed",
  in_transit: "In transit",
  partially_received: "Partially received",
  received: "Received",
  cancelled: "Cancelled",
};

// Header statuses that mean the same as each stage; a header outside its
// stage's list is worth showing (the provider is further along than it says).
const STAGE_HEADERS = {
  placed: null,
  in_transit: ["Picked Up", "In Transit", "Reached Warehouse"],
  partially_received: ["Partially Received"],
  received: ["Stock-in Complete"],
  cancelled: ["Cancelled", "Returned to Supplier"],
};
const ALL_LATER_HEADERS = Object.values(STAGE_HEADERS).flat().filter(Boolean);

export const headerDisagrees = (stage, status) => {
  if (!status) return false;
  const headers = STAGE_HEADERS[stage];
  return headers ? !headers.includes(status) : ALL_LATER_HEADERS.includes(status);
};

export const TIMING_LABEL = {
  scheduled: "On schedule",
  overdue: "Overdue",
  unconfirmed: "Stale (>30d)",
  no_date: "No date",
};

/** Base params every endpoint takes. */
export const baseParams = ({ clientId, warehouseId, months }) => {
  const params = { warehouse_id: warehouseId, months };
  if (clientId) params.client_id = clientId;
  return params;
};

/** Params for one page of a table. `page` in the UI is 0-based. */
export const buildRowsParams = ({ base, table, page = 0, rowsPerPage = 10, sort, dir, search, ...filters }) => {
  const params = { ...base, table, page: page + 1, limit: rowsPerPage };
  if (sort) params.sort = sort;
  if (dir) params.dir = dir;
  const trimmed = String(search || "").trim();
  if (trimmed) params.search = trimmed;
  Object.entries(filters).forEach(([key, value]) => {
    if (value != null && value !== "" && value !== "all") params[key] = value;
  });
  return params;
};

export const fmtPct = (value) =>
  value == null || !Number.isFinite(Number(value)) ? "—" : `${Math.round(Number(value))}%`;

export const fmtLead = (value) =>
  value == null || !Number.isFinite(Number(value)) ? "—" : `${Math.round(Number(value))}d`;

export const fmtCompact = (value) => {
  const number = Number(value);
  if (value == null || !Number.isFinite(number)) return "—";
  if (Math.abs(number) >= 1000000) return `${(number / 1000000).toFixed(1)}M`;
  if (Math.abs(number) >= 10000) return `${Math.round(number / 1000)}K`;
  if (Math.abs(number) >= 1000) return `${(number / 1000).toFixed(1)}K`;
  return fmtUnits(number);
};

const asUtc = (value) => new Date(`${String(value).slice(0, 10)}T00:00:00.000Z`);

export const fmtDay = (value) => {
  if (!value) return "—";
  const date = asUtc(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

export const fmtShortDay = (value) => {
  if (!value) return "—";
  const date = asUtc(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
};

export const fmtMonth = (value) => {
  if (!value) return "—";
  const date = asUtc(`${value}-01`);
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });
};

/** "Week of Oct 26" / bucket labels for the inbound calendar. */
export const calendarLabel = (bucket) => {
  if (bucket.kind === "week") return fmtShortDay(bucket.start);
  if (bucket.kind === "later") return "Later";
  if (bucket.kind === "unconfirmed") return "Stale >30d";
  if (bucket.kind === "overdue") return "Overdue";
  return "No date";
};

/** Plain-language sentence for a SKU's coverage row. */
export const coverageSentence = (row) => {
  if (!row) return "";
  switch (row.status) {
    case "covered":
      return `${row.next_po_no} lands ${fmtDay(row.next_expected_date)}, before stock runs out ${fmtDay(row.stockout_date)}.`;
    case "late":
      return `${row.next_po_no} lands ${fmtDay(row.next_expected_date)} — ${row.gap_days} days after stock runs out (≈${fmtUnits(row.units_short)} units short).`;
    case "pending":
      return row.days_overdue
        ? `${row.next_po_no || "A PO"} is ${row.days_overdue} days overdue — no firm arrival date.`
        : `${row.next_po_no || "A PO"} is on order with no expected date.`;
    case "unconfirmed":
      return `Only stale POs (${fmtUnits(row.unconfirmed_units)} units overdue >30 days) — not counted as supply.`;
    default:
      return "Nothing on order for this warehouse.";
  }
};

export const purchaseOrderErrorMessage = (err) => {
  const data = err?.response?.data;
  if (data?.data?.code === "WAREHOUSE_REQUIRED") {
    return "Select a warehouse — purchase orders are analysed for one warehouse at a time.";
  }
  return data?.message || err?.message || "Unable to load purchase order analysis.";
};
